import React from 'react';
import {
  Search,
  Plus,
  Inbox,
  MoreVertical,
  ChevronRight,
  Filter
} from 'lucide-react';
import './ResponsiveDataList.css';

/**
 * ResponsiveDataList
 * 
 * An adaptive data view component that:
 * - Stacks Search Bar and Action Button vertically on mobile, side-by-side on desktop
 * - Seamlessly displays standard Tabular view on Desktop (>= 768px) and Modern Stacked Card Items on Mobile (< 768px)
 * - Centered empty state with helper text and vectors
 * - Accessible, responsive, zero-layout-shift design
 */
export default function ResponsiveDataList({
  title = '',
  subtitle = '',
  data = [],
  columns = [],
  onAddNew = null,
  addNewLabel = '+ Add New',
  searchTerm = '',
  onSearchChange = () => {},
  searchPlaceholder = 'Search records...',
  loading = false,
  emptyStateTitle = 'No records found',
  emptyStateSubtitle = 'Try adjusting your search query or add a new record to get started.',
  emptyStateIcon = null,
  renderMobileCard = null,
  primaryKey = 'id',
  className = ''
}) {
  return (
    <div className={`rdl-container ${className}`}>
      {/* 1. SECTION HEADER & ACTION BAR */}
      {(title || subtitle || onAddNew || onSearchChange) && (
        <div className="rdl-top-bar">
          <div className="rdl-title-block">
            {title && <h2 className="rdl-section-title">{title}</h2>}
            {subtitle && <p className="rdl-section-subtitle">{subtitle}</p>}
          </div>

          {/* Search & Action Wrapper */}
          <div className="rdl-controls-wrapper">
            {/* Mobile Stacked / Desktop Side-by-Side */}
            {onAddNew && (
              <button
                type="button"
                className="rdl-btn-primary"
                onClick={onAddNew}
                aria-label={addNewLabel}
              >
                <Plus className="w-5 h-5 mr-1.5" />
                <span>{addNewLabel}</span>
              </button>
            )}

            {onSearchChange && (
              <div className="rdl-search-box">
                <Search className="rdl-search-icon" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="rdl-search-input"
                  aria-label={searchPlaceholder}
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="rdl-search-clear"
                    onClick={() => onSearchChange('')}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 2. CONTENT AREA */}
      {loading ? (
        <div className="rdl-loading-state">
          <div className="rdl-spinner" />
          <p>Loading data...</p>
        </div>
      ) : !data || data.length === 0 ? (
        /* 3. VERTICALLY CENTERED EMPTY STATE */
        <div className="rdl-empty-state">
          <div className="rdl-empty-icon-wrap">
            {emptyStateIcon || <Inbox className="w-10 h-10 text-blue-500" />}
          </div>
          <h3 className="rdl-empty-title">{emptyStateTitle}</h3>
          <p className="rdl-empty-subtitle">{emptyStateSubtitle}</p>
          {onAddNew && (
            <button
              type="button"
              className="rdl-btn-secondary mt-4"
              onClick={onAddNew}
            >
              <Plus className="w-4 h-4 mr-1.5" />
              {addNewLabel}
            </button>
          )}
        </div>
      ) : (
        <>
          {/* 4. DESKTOP TABULAR VIEW (>= 768px) */}
          <div className="rdl-desktop-table-wrap">
            <table className="rdl-table">
              <thead>
                <tr>
                  {columns.map((col, idx) => (
                    <th key={col.accessor || col.header || idx} className={col.headerClassName || ''}>
                      {col.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, rowIdx) => {
                  const keyVal = row[primaryKey] || row.id || rowIdx;
                  return (
                    <tr key={keyVal}>
                      {columns.map((col, colIdx) => {
                        let cellContent = null;
                        if (col.render) {
                          cellContent = col.render(row, rowIdx);
                        } else if (typeof col.accessor === 'function') {
                          cellContent = col.accessor(row);
                        } else if (col.accessor) {
                          cellContent = row[col.accessor];
                        }

                        return (
                          <td key={col.accessor || colIdx} className={col.cellClassName || ''}>
                            {cellContent !== undefined && cellContent !== null ? cellContent : '-'}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* 5. MOBILE MODERN CARD ITEMS VIEW (< 768px) */}
          <div className="rdl-mobile-cards-wrap">
            {data.map((row, rowIdx) => {
              const keyVal = row[primaryKey] || row.id || rowIdx;

              if (renderMobileCard) {
                return (
                  <div key={keyVal} className="rdl-mobile-card-custom">
                    {renderMobileCard(row, rowIdx)}
                  </div>
                );
              }

              // Default Smart Mobile Card item generated from columns
              const primaryCol = columns[0];
              const actionsCol = columns.find((c) => String(c.header).toLowerCase() === 'actions');
              const bodyCols = columns.filter((c) => c !== primaryCol && c !== actionsCol);

              const primaryTitle = primaryCol
                ? primaryCol.render
                  ? primaryCol.render(row, rowIdx)
                  : row[primaryCol.accessor]
                : `Item #${rowIdx + 1}`;

              const actionsContent = actionsCol
                ? actionsCol.render
                  ? actionsCol.render(row, rowIdx)
                  : row[actionsCol.accessor]
                : null;

              return (
                <article key={keyVal} className="rdl-mobile-card-item">
                  <div className="rdl-card-header">
                    <div className="rdl-card-primary-title">
                      {primaryTitle}
                    </div>
                  </div>

                  <div className="rdl-card-body-grid">
                    {bodyCols.map((col, colIdx) => {
                      let value = null;
                      if (col.render) {
                        value = col.render(row, rowIdx);
                      } else if (typeof col.accessor === 'function') {
                        value = col.accessor(row);
                      } else if (col.accessor) {
                        value = row[col.accessor];
                      }

                      if (value === undefined || value === null || value === '') return null;

                      return (
                        <div key={col.accessor || colIdx} className="rdl-card-field">
                          <span className="rdl-field-label">{col.header}:</span>
                          <div className="rdl-field-value">{value}</div>
                        </div>
                      );
                    })}
                  </div>

                  {actionsContent && (
                    <div className="rdl-card-actions">
                      {actionsContent}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
